import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of, Observer} from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { CookieService } from 'ngx-cookie-service';

import { CognitoCallback, CognitoUtil } from './cognito.service';
import { AuthenticationDetails, CognitoUser, CognitoUserSession } from 'amazon-cognito-identity-js';
import * as AWS from 'aws-sdk/global';
import * as STS from 'aws-sdk/clients/sts';

import { environment } from '../../../environments/environment';

const httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' })
};

@Injectable()
export class UserLoginService {
    public static env = environment.production;

    mfaStep = false;
    mfaData = {
        destination: '',
        callback: null
    };

    constructor(
        public cognitoUtil: CognitoUtil,
        private router: Router,
        private http: HttpClient,
        private cookieService: CookieService) {
    }

    public async authenticate(callback: any, username: string, password: string) {
        // console.log('UserLoginService: starting the authentication');
        const authenticationData = {
            Username: username,
            Password: password,
        };
        const authenticationDetails = new AuthenticationDetails(authenticationData);

        const userData = {
            Username: username,
            Pool: this.cognitoUtil.getUserPool()
        };

        // console.log('UserLoginService: Params set...Authenticating the user');
        const cognitoUser = new CognitoUser(userData);
        // console.log('UserLoginService: config is ' + AWS.config);
        await cognitoUser.authenticateUser(authenticationDetails, {
            
            newPasswordRequired: (userAttributes, requiredAttributes) => {
                delete userAttributes.email_verified;
                delete userAttributes.email;
                delete userAttributes.phone_number_verified;
                cognitoUser.completeNewPasswordChallenge(password, userAttributes, {
                    onSuccess: result => {
                        this.validateUser(result.getIdToken(), null);
                    },
                    onFailure: err => {
                        callback.cognitoCallback('', null);
                    }
                });
            },
            onSuccess: result => {
                // confirm with backend
                this.validateUser(result.getIdToken(), null);
            },
            onFailure: err => {
                // console.log(err);
                callback.cognitoCallback('', null);
            }/*,
            mfaRequired: (challengeName, challengeParameters) => {

                this.mfaStep = true;
                this.mfaData.destination = challengeParameters.CODE_DELIVERY_DESTINATION;
                this.mfaData.callback = (code: string) => {
                    if (code == null || code.length === 0) {
                        // this.errorMessage = 'Code is required';
                        return;
                    }

                    // this.errorMessage = null;
                };

                callback.handleMFAStep(challengeName, challengeParameters, (confirmationCode: string) => {
                    cognitoUser.sendMFACode(confirmationCode, {
                        onSuccess: result => this.onLoginSuccess(callback, result),
                        onFailure: err => this.onLoginError(callback, err)
                    });
                });
            }*/
        });
    }

    public logout() {
        const cogUser = this.cognitoUtil.getCurrentUser();
        if (cogUser) {
            this.cognitoUtil.getCurrentUser().signOut();
            //this.cookieService.delete('tok');
            localStorage.removeItem('tok')
        }

        location.reload();
    }

    public isAuthenticated(observer, option: any) {
        const cognitoUser = this.cognitoUtil.getCurrentUser();
        const that = this;
        if (cognitoUser != null) {
            cognitoUser.getSession((err, session) => {
                if (err) {
                    that.router.navigate(['login']);
                }

                this.validateUser(session.getIdToken(), option);
            });
        } else {
            if (this.router.url !== '/login') {
                this.router.navigate(['login']);
            }
        }
    }

    public validateUser(session: any, fromDashboard: any) {
        let path = '/login';
        if (session && session.payload.email) {
            //this.cookieService.set('tok', session.payload.email, undefined, undefined, undefined, true, 'None');
            let _email = session.payload.email;
            if(_email && _email.includes('nearshoretechnology')){
                _email = _email.replace('nearshoretechnology','blueriver');
            }
            localStorage.setItem('tok', _email);
            if (this.router.url !== '/dashboard') {
                path = '/dashboard';
            }
        }

        this.route(path);
    }

    private route(path: any) {
        if (path) {
            this.router.navigate([path]);
        }
    }

    private handleError<T>(operation = 'operation', result?: T) {
        return (error: any): Observable<T> => {
            console.error(error); // log to console instead
            return of(result as T);
        };
    }

    private log(message: string) {
        if (!UserLoginService.env) {
            console.log(message);
        }
    }

    public forgotPassword(username: string, callback: CognitoCallback) {
        const userData = {
            Username: username,
            Pool: this.cognitoUtil.getUserPool()
        };

        const cognitoUser = new CognitoUser(userData);

        cognitoUser.forgotPassword({
            onSuccess: (dat) => {
                console.log(dat);
            },
            onFailure: (err) => {
                callback.cognitoCallback('exeed', err.message);
            },
            inputVerificationCode: (data) => {
                if (data) {
                    callback.cognitoCallback('vcode', data.CodeDeliveryDetails.Destination);
                }
            }
        });
    }

    public confirmNewPassword(email: string, verificationCode: string, password: string, callback: CognitoCallback) {
        const userData = {
            Username: email,
            Pool: this.cognitoUtil.getUserPool()
        };

        const cognitoUser = new CognitoUser(userData);

        cognitoUser.confirmPassword(verificationCode, password, {
            onSuccess: () => {
                callback.cognitoCallback('success', null);
            },
            onFailure: (err) => {
                callback.cognitoCallback(err.message, null);
            }
        });
    }

    private onLoginSuccess = (callback: CognitoCallback, session: CognitoUserSession) => {

        // console.log('In authenticateUser onSuccess callback');

        AWS.config.credentials = this.cognitoUtil.buildCognitoCreds(session.getIdToken().getJwtToken());

        // So, when CognitoIdentity authenticates a user, it doesn't actually hand us the IdentityID,
        // used by many of our other handlers. This is handled by some sly underhanded calls to AWS Cognito
        // API's by the SDK itself, automatically when the first AWS SDK request is made that requires our
        // security credentials. The identity is then injected directly into the credentials object.
        // If the first SDK call we make wants to use our IdentityID, we have a
        // chicken and egg problem on our hands. We resolve this problem by 'priming' the AWS SDK by calling a
        // very innocuous API call that forces this behavior.
        const clientParams: any = {};
        /*if (environment.sts_endpoint) {
            clientParams.endpoint = environment.sts_endpoint;
        }*/
        const sts = new STS(clientParams);
        sts.getCallerIdentity((err, data) => {
            // console.log('UserLoginService: Successfully set the AWS credentials');
            callback.cognitoCallback(null, session);
        });
    }

    private onLoginError = (callback: CognitoCallback, err) => {
        callback.cognitoCallback(err.message, null);
    }
}
